import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '../../lib/api';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminPagination } from '../../components/AdminPagination';
import { AdminSearchBar } from '../../components/AdminSearchBar';

const ADDON_TYPES = [
  { value: 'PER_TRIP', label: 'Por Viaje' },
  { value: 'PER_PERSON', label: 'Por Persona' },
  { value: 'PER_UNIT', label: 'Por Unidad' }
];

export const AdminAddonsPage = () => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'PER_TRIP',
    price: '',
    currency: 'USD',
    min_qty: '0',
    max_qty: '10',
    image_url: '',
    is_active: true
  });

  const loadAddons = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAddons(page, 20, search);
      setAddons(response.data.items || []);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Error loading addons:', error);
      toast.error('Error al cargar extras');
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [page, search]);

  useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };
  
  const getTypeLabel = (type) => {
    const found = ADDON_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        name: formData.name,
        description: formData.description,
        type: formData.type,
        price: parseFloat(formData.price),
        currency: formData.currency,
        min_qty: parseInt(formData.min_qty),
        max_qty: formData.max_qty ? parseInt(formData.max_qty) : null,
        image_url: formData.image_url,
        is_active: formData.is_active
      };
      
      if (editingAddon) {
        await adminApi.updateAddon(editingAddon.id, data);
        toast.success('Extra actualizado exitosamente');
      } else {
        await adminApi.createAddon(data);
        toast.success('Extra creado exitosamente');
      }
      
      setDialogOpen(false);
      setEditingAddon(null);
      resetForm();
      loadAddons();
    } catch (error) {
      console.error('Error saving addon:', error);
      toast.error('Error al guardar el extra');
    }
  };
  
  const handleEdit = (addon) => {
    setEditingAddon(addon);
    setFormData({
      name: addon.name,
      description: addon.description || '',
      type: addon.type || 'PER_TRIP',
      price: addon.price.toString(),
      currency: addon.currency,
      min_qty: (addon.min_qty || 0).toString(),
      max_qty: addon.max_qty ? addon.max_qty.toString() : '',
      image_url: addon.image_url || '',
      is_active: addon.is_active
    });
    setDialogOpen(true);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este extra?')) return;
    
    try {
      await adminApi.deleteAddon(id);
      toast.success('Extra eliminado exitosamente');
      loadAddons();
    } catch (error) {
      console.error('Error deleting addon:', error);
      toast.error('Error al eliminar el extra');
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'PER_TRIP',
      price: '',
      currency: 'USD',
      min_qty: '0',
      max_qty: '10',
      image_url: '',
      is_active: true
    });
  };
  
  if (loading && !hasLoadedRef.current) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-manrope font-bold text-3xl text-emerald-950">Extras / Addons</h1>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <AdminSearchBar value={search} onChange={handleSearch} placeholder="Buscar extra..." />
          <Button
            onClick={() => {
              resetForm();
              setEditingAddon(null);
              setDialogOpen(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 shrink-0"
            data-testid="add-addon-btn"
          >
            <Plus className="w-4 h-4" />
            Agregar
          </Button>
        </div>
      </div>
      
      <div className={`bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden transition-opacity ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Imagen</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Nombre</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Tipo</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Precio</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Cantidad</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {addons.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-500">
                  {search ? `No se encontraron extras para "${search}"` : 'No hay extras registrados'}
                </td></tr>
              )}
              {addons.map((addon) => (
                <tr key={addon.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    {addon.image_url ? (
                      <img src={addon.image_url} alt={addon.name} className="w-16 h-16 object-cover rounded" />
                    ) : (
                      <div className="w-16 h-16 bg-emerald-50 rounded flex items-center justify-center text-emerald-600 text-xs font-medium">
                        Sin imagen
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{addon.name}</p>
                    {addon.description && <p className="text-xs text-slate-500">{addon.description}</p>}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm px-2 py-1 bg-blue-50 text-blue-700 rounded">
                      {getTypeLabel(addon.type || 'PER_TRIP')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-lg font-bold text-emerald-600">${addon.price} USD</p>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    Min: {addon.min_qty || 0} / Max: {addon.max_qty || '∞'}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full
                      ${addon.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                      {addon.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => handleEdit(addon)}
                        className="p-2 hover:bg-slate-100 rounded transition-colors"
                      >
                        <Edit className="w-4 h-4 text-slate-600" />
                      </button>
                      <button 
                        onClick={() => handleDelete(addon.id)}
                        className="p-2 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingAddon ? 'Editar' : 'Agregar'} Extra</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Nombre *</label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Silla de bebé"
                required
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Descripción</label>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descripción del extra"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Tipo de cobro *</label>
              <Select 
                value={formData.type} 
                onValueChange={(value) => setFormData({...formData, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADDON_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {formData.type === 'PER_TRIP' && 'Se cobra una vez por viaje'}
                {formData.type === 'PER_PERSON' && 'Se cobra por cada pasajero'}
                {formData.type === 'PER_UNIT' && 'Se cobra por unidad seleccionada'}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Precio *</label>
                <Input 
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  placeholder="10.00"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Moneda</label>
                <div className="h-10 px-3 bg-slate-100 border border-slate-200 rounded-md flex items-center text-sm text-slate-700 font-medium">
                  USD (Dólar estadounidense)
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Cantidad Mínima</label>
                <Input 
                  type="number"
                  min="0"
                  value={formData.min_qty}
                  onChange={(e) => setFormData({...formData, min_qty: e.target.value})}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Cantidad Máxima</label>
                <Input 
                  type="number"
                  min="1"
                  value={formData.max_qty}
                  onChange={(e) => setFormData({...formData, max_qty: e.target.value})}
                  placeholder="10 (vacío = sin límite)"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">URL de Imagen <span className="text-slate-400 font-normal">(opcional)</span></label>
              <Input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://... (dejar vacío si no aplica)"
              />
              {formData.image_url && (
                <div className="mt-2 border rounded-lg overflow-hidden bg-slate-50" style={{maxHeight: '150px'}}>
                  <img
                    src={formData.image_url}
                    alt="Vista previa"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  <div className="items-center justify-center gap-2 text-red-500 p-3 text-sm" style={{display: 'none'}}>
                    No se pudo cargar la imagen. Verifique la URL.
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Extra activo</label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {editingAddon ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
